import {
  ExternalLink,
  BookOpen,
  Users,
  Calendar,
  Quote,
  Download,
  Lock,
  Unlock,
  FileText,
  FileSearch,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { SemanticScholarPaper } from '@/features/search/hooks/useSearch';

interface PaperCardProps {
  paper: SemanticScholarPaper;
  onImport: (paper: SemanticScholarPaper) => void;
  isImporting?: boolean;
}

export function PaperCard({ paper, onImport, isImporting }: PaperCardProps) {
  const authorText =
    paper.authors.length > 3
      ? `${paper.authors
          .slice(0, 3)
          .map((a) => a.name)
          .join(', ')} et al.`
      : paper.authors.map((a) => a.name).join(', ');

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {paper.isOpenAccess ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Unlock className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                    </TooltipTrigger>
                    <TooltipContent>Open Access</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Lock className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    </TooltipTrigger>
                    <TooltipContent>Not Open Access</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <h3 className="font-semibold text-slate-900 text-sm leading-tight line-clamp-2">
                {paper.title}
              </h3>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 mt-2">
              {authorText && (
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  <span className="truncate max-w-[250px]">{authorText}</span>
                </span>
              )}
              {paper.year && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {paper.year}
                </span>
              )}
              {paper.journal && (
                <span className="flex items-center gap-1">
                  <BookOpen className="h-3 w-3" />
                  <span className="truncate max-w-[200px]">{paper.journal.name}</span>
                </span>
              )}
              <span className="flex items-center gap-1">
                <Quote className="h-3 w-3" />
                {paper.citationCount} citations
              </span>
            </div>
          </div>

          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                onClick={() => onImport(paper)}
                disabled={isImporting}
              >
                <Download className="h-3.5 w-3.5 mr-1" />
                Import
              </Button>
              {paper.url && (
                <Button
                  size="sm"
                  variant="ghost"
                  asChild
                >
                  <a href={paper.url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </Button>
              )}
            </div>
            {paper.openAccessPdf ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
                      <FileText className="h-3 w-3" />
                      PDF link available
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[220px]">
                    <p className="text-xs">Open access PDF link found. Full text extraction will be attempted, but some publishers may block automatic downloads. Abstract will be used as fallback.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : paper.abstract ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex items-center gap-1 text-[10px] text-amber-500 font-medium">
                      <FileSearch className="h-3 w-3" />
                      Abstract only
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">No PDF available — only the abstract will be imported</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex items-center gap-1 text-[10px] text-slate-400 font-medium">
                      <FileSearch className="h-3 w-3" />
                      Metadata only
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">No PDF or abstract — only title, authors, and DOI will be imported</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>

        {(paper.abstract || paper.tldr) && (
          <p className="text-xs text-slate-600 line-clamp-3">
            {paper.tldr?.text ?? paper.abstract}
          </p>
        )}

        <div className="flex flex-wrap gap-1.5">
          {paper.externalIds?.DOI && (
            <Badge variant="outline" className="text-[10px]">
              DOI: {paper.externalIds.DOI}
            </Badge>
          )}
          {paper.fieldsOfStudy?.slice(0, 3).map((field) => (
            <Badge key={field} variant="secondary" className="text-[10px]">
              {field}
            </Badge>
          ))}
          {paper.publicationTypes?.map((type) => (
            <Badge key={type} variant="secondary" className="text-[10px]">
              {type}
            </Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
